// Orders Dashboard — reworked to mirror Talmia-1's column set.
//
// UI layout:
//   [ Header · Sync orders · Reload ]
//   [ Preset chips: Default · Parked · Split · Refund · Multi Ships · … ]
//   [ Filter row: Search · Source · Status · Supplier · VIP · Period · AM(grey) · Reset ]
//   [ KPI strip: Orders · Value · Avg ]
//   [ Table with stacked cells + hover-tooltip on Staff/Order Notes ]
//   [ Pager ]
//
// Row click → OrderDetailModal (horizontal sub-tabs).
// AI 360 button per row → AITimelineModal.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import {
  Search, RefreshCw, ExternalLink, X, Package, PhoneCall, FileText, Truck,
  PoundSterling, Receipt, ChevronLeft, ChevronRight, StickyNote, MessageSquare,
  BadgeCheck, Filter, RotateCcw, User, AlertTriangle, Sparkles, Flag, ChevronDown, Check,
} from "lucide-react";
import SyncButton from "../../components/SyncButton";
import InfiniteSentinel from "../../components/InfiniteSentinel";
import LiveCount from "../../components/LiveCount";
import JumpRail from "../../components/JumpRail";
import { AITimelineButton, AITimelineModal } from "./AIOrderTimeline";
import { OwnerChip } from "../../components/OwnerChip";
import Person360Overlay from "../../components/Person360Overlay";
import {
  API, token, money, fmtDate, STATUS_TONE, TIER_TONE, HoverNote,
} from "./_shared";

const PERIOD_OPTIONS = [
  { key: "",   label: "All time" },
  { key: 7,    label: "Last 7 days" },
  { key: 30,   label: "Last 30 days" },
  { key: 90,   label: "Last 90 days" },
  { key: 180,  label: "Last 6 months" },
  { key: 365,  label: "Last 12 months" },
];

const DEFAULT_FILTERS = { q: "", view: "default", status: "", source: "", supplier: "", vip: "", period_days: "", account_manager: "" };

export default function OrdersDashboard() {
  const LIMIT = 50;
  const queryClient = useQueryClient();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState(null);
  const [facets, setFacets] = useState({ sources: [], suppliers: [], statuses: [], vip_levels: [] });
  const [presets, setPresets] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [aiOrderId, setAiOrderId] = useState(null);
  const [jumpedOffset, setJumpedOffset] = useState(0);
  const skipRef = useRef(0);
  const loadingRef = useRef(false);
  const genRef = useRef(0);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchAt = useCallback(async (skip, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    const gen = genRef.current;
    const f = filtersRef.current;
    try {
      const params = new URLSearchParams({
        ...(f.q && { q: f.q }),
        ...(f.view && f.view !== "default" && { view: f.view }),
        ...(f.status && { status: f.status }),
        ...(f.source && { source: f.source }),
        ...(f.supplier && { supplier: f.supplier }),
        ...(f.vip && { vip: f.vip }),
        ...(f.period_days && { period_days: f.period_days }),
        limit: LIMIT, skip,
      });
      const url = `${API}/orders/dashboard?${params.toString()}`;
      // react-query cache: identical filter/page combos served from cache, no refetch.
      const data = await queryClient.fetchQuery({
        queryKey: ["orders-dashboard", params.toString()],
        queryFn: () => axios.get(url, { headers: { Authorization: `Bearer ${token()}` } }).then((res) => res.data),
        staleTime: append ? 120000 : 0,
        gcTime: 300000,
      });
      if (gen !== genRef.current) return;
      const batch = data.items || [];
      const t = data.total_in_query || 0;
      setItems((prev) => (append ? [...prev, ...batch] : batch));
      setTotal(t);
      setKpis(data.kpis || null);
      setFacets(data.facets || {});
      setPresets(data.presets || []);
      skipRef.current = skip + batch.length;
      setHasMore(skip + batch.length < t);
    } catch (e) {
      if (gen === genRef.current) toast.error("Failed to load orders", { description: e?.response?.data?.detail || "" });
    } finally {
      if (gen === genRef.current) { loadingRef.current = false; setLoading(false); }
    }
  }, [queryClient]);

  // Reset + first page whenever any filter changes (debounced).
  useEffect(() => {
    genRef.current += 1;
    skipRef.current = 0;
    loadingRef.current = false;
    setHasMore(true);
    setJumpedOffset(0);
    const t = setTimeout(() => fetchAt(0, false), 250);
    return () => clearTimeout(t);
  }, [filters.q, filters.status, filters.source, filters.supplier, filters.vip, filters.period_days, filters.view, fetchAt]);

  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) fetchAt(skipRef.current, true);
  }, [hasMore, fetchAt]);

  const jumpTo = useCallback((offset) => {
    const aligned = Math.max(0, offset - (offset % LIMIT));
    genRef.current += 1;
    skipRef.current = 0;
    loadingRef.current = false;
    setHasMore(true);
    setJumpedOffset(aligned);
    fetchAt(aligned, false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [fetchAt]);

  const load = useCallback(() => {
    genRef.current += 1;
    skipRef.current = 0;
    loadingRef.current = false;
    setHasMore(true);
    fetchAt(0, false);
  }, [fetchAt]);

  const resetJump = useCallback(() => {
    setJumpedOffset(0);
    load();
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [load]);

  const reset = () => { setFilters(DEFAULT_FILTERS); };

  const nonDefault = useMemo(() => {
    return Object.entries(filters).some(([k, v]) => v && v !== DEFAULT_FILTERS[k]);
  }, [filters]);

  // Client-side sort of the loaded rows (mirrors the deals-table pattern).
  const [sortKey, setSortKey] = useState("order_date");
  const [sortDir, setSortDir] = useState("desc");
  const onSort = useCallback((k) => {
    setSortDir((prevDir) => (sortKey === k ? (prevDir === "asc" ? "desc" : "asc") : "asc"));
    setSortKey(k);
  }, [sortKey]);
  const sortedItems = useMemo(() => {
    if (!sortKey) return items;
    const arr = [...items];
    const dt = (v) => (v ? new Date(v).getTime() || 0 : 0);
    arr.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === "order_date" || sortKey === "delivery_date") { av = dt(av); bv = dt(bv); }
      av = av == null ? -Infinity : av;
      bv = bv == null ? -Infinity : bv;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [items, sortKey, sortDir]);

  // ── Row virtualization (window scroll) — only on-screen rows stay mounted,
  //    so DOM node count stays bounded no matter how many batches are loaded.
  const tableWrapRef = useRef(null);
  const [tableOffset, setTableOffset] = useState(0);
  useLayoutEffect(() => {
    const measure = () => setTableOffset(tableWrapRef.current?.offsetTop ?? 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const rowVirtualizer = useWindowVirtualizer({
    count: sortedItems.length,
    estimateSize: () => 44,
    overscan: 12,
    getItemKey: (i) => sortedItems[i]?.id ?? i,
    scrollMargin: tableOffset,
  });
  const vItems = rowVirtualizer.getVirtualItems();
  const padTop = vItems.length > 0 ? Math.max(0, vItems[0].start - rowVirtualizer.options.scrollMargin) : 0;
  const padBottom = vItems.length > 0 ? Math.max(0, rowVirtualizer.getTotalSize() - vItems[vItems.length - 1].end) : 0;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="orders-dashboard">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Package className="w-5 h-5 text-emerald-300" />
        <h1 className="text-xl font-semibold text-white">Orders Dashboard</h1>
        <LiveCount shown={items.length} total={total} noun="order" testId="orders-live-count" />
        <div className="ml-auto flex items-center gap-2">
          <SyncButton resource="orders" />
          <button onClick={load} className="p-1.5 rounded hover:bg-white/[0.06] text-slate-400 hover:text-white"
            title="Reload" data-testid="orders-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filter row — all default to "All"; multi-select dropdowns */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="orders-filters">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input type="text" placeholder="Search customer, email, order#, postcode, company, supplier…"
            value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="w-full pl-8 pr-3 py-1.5 rounded border border-white/10 bg-white/[0.02] text-slate-100 text-[13px] placeholder-slate-500 focus:border-emerald-500/40 focus:outline-none"
            data-testid="orders-search"
          />
        </div>
        <FMulti value={filters.source}   onChange={(v) => setFilters({ ...filters, source: v })}
          options={facets.sources}    label="Source"    testid="orders-source-filter" />
        <FMulti value={filters.status}   onChange={(v) => setFilters({ ...filters, status: v })}
          options={facets.statuses}   label="Status"    testid="orders-status-filter" />
        <FMulti value={filters.supplier} onChange={(v) => setFilters({ ...filters, supplier: v })}
          options={facets.suppliers}  label="Supplier"  testid="orders-supplier-filter" />
        <FMulti value={filters.vip}      onChange={(v) => setFilters({ ...filters, vip: v })}
          options={facets.vip_levels} label="VIP"       testid="orders-vip-filter" />
        <select value={filters.period_days}
          onChange={(e) => setFilters({ ...filters, period_days: e.target.value })}
          className="px-2 py-1.5 rounded border border-white/10 bg-white/[0.02] text-slate-200 text-[12px] max-w-[150px]"
          data-testid="orders-period-filter">
          {PERIOD_OPTIONS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <select disabled title="Coming soon — Raj to wire AccountManager onto orders"
          className="px-2 py-1.5 rounded border border-white/10 bg-white/[0.02] text-slate-600 text-[12px] max-w-[180px] cursor-not-allowed opacity-60"
          data-testid="orders-am-filter">
          <option>By AM (360) · TBD</option>
        </select>
        {nonDefault && (
          <button onClick={reset}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded border border-rose-500/30 bg-rose-500/10 text-rose-200 text-[10.5px] font-mono uppercase tracking-widest hover:bg-rose-500/20"
            data-testid="orders-reset">
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        )}
        <Filter className="w-3.5 h-3.5 text-slate-500 ml-1" />
      </div>

      {/* KPI strip */}
      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="orders-kpis">
          <KpiCard label="Orders in view"   value={kpis.orders_in_view}          icon={Receipt}       tone="text-emerald-300" />
          <KpiCard label="Value in view"    value={money(kpis.value_in_view)}    icon={PoundSterling} tone="text-amber-300" />
          <KpiCard label="Avg order value"  value={money(kpis.avg_order_in_view)} icon={Truck}        tone="text-sky-300" />
        </div>
      )}

      {/* Table — full column set per the Orders Dashboard mockup */}
      <div className="rounded-lg border border-white/5 bg-white/[0.02] overflow-hidden" data-testid="orders-table">
        <div className="overflow-x-auto" ref={tableWrapRef}>
          <table className="w-full min-w-[1620px] border-separate border-spacing-0 text-[12px]">
            <thead>
              <tr>
                <Th>Order#</Th>
                <Th sort="order_date" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Order Date</Th>
                <Th>Src</Th>
                <Th>Pay</Th>
                <Th align="center">PO</Th>
                <Th align="center">Conf</Th>
                <Th sort="delivery_date" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Delivery</Th>
                <Th align="center">Notes</Th>
                <Th>VIP</Th>
                <Th>Customer</Th>
                <Th>Postcode</Th>
                <Th>Supplier</Th>
                <Th sort="cust_price_inc_vat" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right">Cust £ (inc)</Th>
                <Th sort="po_cost_inc_vat" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right">PO £ (inc)</Th>
                <Th sort="supp_cost_inc_vat" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right">Supp £ (inc)</Th>
                <Th sort="po_cost_diff" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right">PO Diff</Th>
                <Th>Flags</Th>
              </tr>
            </thead>
            <tbody>
              {padTop > 0 && <tr aria-hidden="true"><td colSpan={17} style={{ height: padTop, padding: 0, border: 0 }} /></tr>}
              {vItems.map((vr) => {
                const o = sortedItems[vr.index];
                return (
                  <OrderRow
                    key={o.id}
                    o={o}
                    onOpen={() => setOpenId(o.id)}
                    innerRef={rowVirtualizer.measureElement}
                    dataIndex={vr.index}
                  />
                );
              })}
              {padBottom > 0 && <tr aria-hidden="true"><td colSpan={17} style={{ height: padBottom, padding: 0, border: 0 }} /></tr>}
            </tbody>
          </table>
        </div>
        {loading && items.length === 0 && <div className="text-center text-slate-500 py-8">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-center text-slate-500 py-10 text-[13px]">
            No orders match those filters. Try Reset or hit Sync at the top-right.
          </div>
        )}
        {items.length > 0 && (
          <InfiniteSentinel
            onReach={loadMore}
            hasMore={hasMore}
            loading={loading}
            count={items.length}
            total={total}
            testId="orders-sentinel"
          />
        )}
      </div>

      {openId && <OrderDetailModal orderId={openId} onClose={() => setOpenId(null)} onOpenAI={() => setAiOrderId(openId)} />}
      {aiOrderId && <AITimelineModal orderId={aiOrderId} onClose={() => setAiOrderId(null)} />}
      <JumpRail total={total} atOffset={jumpedOffset} onJump={jumpTo} onReset={resetJump} testId="orders-jump-rail" />
    </div>
  );
}

// ─── Compact row (stacked cells) ────────────────────────────────────────
// ─── Table primitives + pills (mockup fidelity) ─────────────────────────
const TH_BG = "bg-[#0e1524]";
function Th({ children, sort, sortKey, sortDir, onSort, align = "left" }) {
  const active = sort && sortKey === sort;
  const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return (
    <th
      data-testid={sort ? `orders-th-${sort}` : undefined}
      onClick={sort ? () => onSort(sort) : undefined}
      className={`sticky top-0 z-10 ${TH_BG} border-b border-white/10 px-3 py-2 whitespace-nowrap text-[10px] font-mono uppercase tracking-widest text-slate-500 ${alignCls} ${sort ? "cursor-pointer hover:text-slate-300 select-none" : ""}`}
    >
      {children}
      {sort && <span className="ml-1 text-slate-600">{active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</span>}
    </th>
  );
}

function Td({ children, align = "left", mono = false, className = "" }) {
  const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return (
    <td className={`px-3 h-[40px] align-middle whitespace-nowrap border-b border-white/[0.04] ${alignCls} ${mono ? "font-mono tabular-nums" : ""} ${className}`}>
      {children}
    </td>
  );
}

const PILL_TONE = {
  src:  "bg-sky-500/15 text-sky-300 border-sky-500/30",
  pay:  "bg-violet-500/15 text-violet-300 border-violet-500/30",
  park: "bg-pink-500/15 text-pink-300 border-pink-500/30",
  flag: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};
function Pill({ tone, children, title }) {
  return (
    <span title={title} className={`inline-block text-[9.5px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded border whitespace-nowrap ${PILL_TONE[tone] || "border-white/10 text-slate-400"}`}>
      {children}
    </span>
  );
}
function BoolPill({ v }) {
  if (v == null) return <span className="text-slate-700 text-[10px]">—</span>;
  return v
    ? <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/15 text-emerald-300">YES</span>
    : <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-orange-500/30 bg-orange-500/15 text-orange-300">NO</span>;
}
function VipPill({ v }) {
  const key = String(v).toUpperCase();
  return <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded uppercase tracking-widest ${TIER_TONE[key] || "text-slate-400 bg-white/5"}`}>{key}</span>;
}
function srcShort(source, sub) {
  const s = (sub || source || "").trim();
  if (!s) return "—";
  const parts = s.split(/[\s\-·/]+/).filter(Boolean);
  if (parts.length >= 2) return parts.map((p) => p[0]).join("").slice(0, 3).toUpperCase();
  return s.slice(0, 3).toUpperCase();
}
const Dash = () => <span className="text-slate-700 text-[10px]">—</span>;

// ─── Order row (full table, mockup column set) ──────────────────────────
function OrderRow({ o, onOpen, innerRef, dataIndex }) {
  const stop = (e) => e.stopPropagation();
  const diff = o.po_cost_diff;
  return (
    <tr ref={innerRef} data-index={dataIndex} onClick={onOpen} className="cursor-pointer hover:bg-emerald-500/[0.05]" data-testid={`order-row-${o.id}`}>
      <Td className="font-mono text-emerald-300 font-semibold text-[11px]">#{o.order_number}</Td>
      <Td mono className="text-slate-300 text-[11px]">{fmtDate(o.order_date)}</Td>
      <Td>{o.source ? <Pill tone="src" title={`${o.source}${o.sub_source ? " · " + o.sub_source : ""}`}>{srcShort(o.source, o.sub_source)}</Pill> : <Dash />}</Td>
      <Td>{o.payment ? <Pill tone="pay">{o.payment}</Pill> : <Dash />}</Td>
      <Td align="center"><BoolPill v={o.po_sent} /></Td>
      <Td align="center"><BoolPill v={o.confirmed} /></Td>
      <Td mono className="text-[11px]">
        {o.delivery_date ? <span className="text-teal-300">{fmtDate(o.delivery_date)}</span> : <span className="text-pink-300/80">need</span>}
      </Td>
      <Td align="center">
        <span className="inline-flex items-center gap-1.5 justify-center" onClick={stop}>
          {o.staff_notes && <HoverNote text={o.staff_notes} icon={StickyNote} />}
          {o.order_notes && <HoverNote text={o.order_notes} icon={MessageSquare} />}
          {!o.staff_notes && !o.order_notes && <Dash />}
        </span>
      </Td>
      <Td>{o.vip_level ? <VipPill v={o.vip_level} /> : <Dash />}</Td>
      <Td className="max-w-[220px]">
        <div className="text-slate-100 truncate leading-tight">{o.customer_name || "—"}</div>
        {o.company && <div className="text-[10px] font-mono text-slate-500 truncate leading-tight">{o.company}</div>}
      </Td>
      <Td mono className="text-[11px] text-slate-400">{o.postcode || <Dash />}</Td>
      <Td className="max-w-[200px]">
        <div className="text-slate-200 truncate leading-tight text-[11.5px]">{o.supplier || "—"}</div>
        {o.supplier_branch && <div className="text-[10px] font-mono text-slate-500 truncate leading-tight">{o.supplier_branch}</div>}
      </Td>
      <Td align="right" mono className="text-slate-100 text-[12px]">{money(o.cust_price_inc_vat)}</Td>
      <Td align="right" mono className="text-slate-300">{o.po_cost_inc_vat != null ? money(o.po_cost_inc_vat) : <Dash />}</Td>
      <Td align="right" mono className="text-slate-300">{o.supp_cost_inc_vat != null ? money(o.supp_cost_inc_vat) : <Dash />}</Td>
      <Td align="right" mono className={diff == null ? "" : diff < 0 ? "text-rose-400" : "text-emerald-300"}>
        {diff != null ? money(diff) : <Dash />}
      </Td>
      <Td>
        {o.is_parked
          ? <Pill tone="park">Parked{o.parked_reason ? ` · ${o.parked_reason}` : ""}</Pill>
          : (o.flags && o.flags.length
            ? <span className="inline-flex flex-wrap gap-1">{o.flags.slice(0, 3).map((f, i) => <Pill key={i} tone="flag">{f}</Pill>)}</span>
            : <Dash />)}
      </Td>
    </tr>
  );
}

function KpiCard({ label, value, icon: Icon, tone }) {
  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3 flex items-center gap-3">
      <Icon className={`w-6 h-6 ${tone}`} />
      <div>
        <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">{label}</div>
        <div className="text-lg font-semibold text-white tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function FSel({ value, onChange, options, label, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="px-2 py-1.5 rounded border border-white/10 bg-white/[0.02] text-slate-200 text-[12px] max-w-[170px]"
      data-testid={testid}
    >
      <option value="">{label}</option>
      {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

// Multi-select dropdown (CSV value). Defaults to "All" when nothing picked.
function FMulti({ value, onChange, options, label, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const sel = (value || "").split(",").map((s) => s.trim()).filter(Boolean);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const toggle = (o) => {
    const set = new Set(sel);
    set.has(o) ? set.delete(o) : set.add(o);
    onChange(Array.from(set).join(","));
  };
  const btnLabel = sel.length === 0 ? `${label}: All` : sel.length === 1 ? sel[0] : `${label} · ${sel.length}`;
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={testid}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-[12px] max-w-[190px] ${
          sel.length ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-100" : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/25"
        }`}>
        <span className="truncate">{btnLabel}</span>
        <ChevronDown className="w-3 h-3 opacity-60 shrink-0" />
      </button>
      {open && (
        <div data-testid={`${testid}-panel`}
          className="absolute z-50 mt-1 w-[240px] max-h-[320px] overflow-y-auto rounded-lg border border-white/15 bg-[#11161f] shadow-2xl p-1.5">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</span>
            {sel.length > 0 && (
              <button type="button" onClick={() => onChange("")}
                className="text-[10px] font-mono uppercase text-rose-300 hover:text-rose-200" data-testid={`${testid}-clear`}>All</button>
            )}
          </div>
          {(options || []).map((o) => {
            const on = sel.includes(o);
            return (
              <button key={o} type="button" onClick={() => toggle(o)} data-testid={`${testid}-opt-${o}`}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-[12.5px] ${on ? "bg-emerald-500/15 text-emerald-100" : "text-slate-200 hover:bg-white/[0.05]"}`}>
                <span className={`w-3.5 h-3.5 rounded border grid place-items-center shrink-0 ${on ? "bg-emerald-400 border-emerald-400" : "border-white/25"}`}>
                  {on && <Check className="w-2.5 h-2.5 text-black" />}
                </span>
                <span className="flex-1 truncate">{o}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Order Detail Modal — horizontal sub-tabs, view-only.
//
// Tabs: Line Items · Staff Notes · Supp/PO Analysis · System Notes ·
//       Order Notes · Order Files · Order Conf · Xero Transaction · AI 360
export function OrderDetailModal({ orderId, onClose, onOpenAI, context = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("line_items");
  const [c360Id, setC360Id] = useState(null);

  // Reusable primitive (task-module 05a §2): closing returns the user to the
  // exact prior screen when a launch context was supplied (360 / call / quote).
  const close = useCallback(() => {
    if (context?.returnTo) context.returnTo();
    onClose();
  }, [context, onClose]);

  // Dismiss on Esc (spec §1 acceptance).
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  useEffect(() => {
    axios.get(`${API}/orders/${orderId}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error("Failed to load order", { description: e?.response?.data?.detail || "" }); onClose(); })
      .finally(() => setLoading(false));
  }, [orderId, onClose]);

  // Lock background scroll while the modal is open (prevents the page behind
  // from scrolling and keeps the pop-up aligned on very long timelines).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (loading) return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm grid place-items-center">
      <div className="text-slate-400 text-[13px]">Loading order…</div>
    </div>,
    document.body,
  );
  if (!data) return null;

  const o = data.order;
  const p = data.person;
  const items = Array.isArray(o.line_items) ? o.line_items : [];
  const confs = Array.isArray(o.confirmations) ? o.confirmations : [];
  const dels = Array.isArray(o.deliveries) ? o.deliveries : [];
  const suppInvs = Array.isArray(o.supplier_invoices) ? o.supplier_invoices : [];
  const files = Array.isArray(o.files) ? o.files : [];
  const statusTone = STATUS_TONE[o.status] || "text-slate-400 border-white/10 bg-white/5";

  const TABS = [
    { key: "line_items", label: "Line Items",   count: items.length },
    { key: "po_supp",    label: "PO & Supplier" },
    { key: "xero",       label: "Xero",          count: o.xero ? null : 0 },
    { key: "confs",      label: "Confirmations", count: confs.length },
    { key: "deliveries", label: "Deliveries",    count: dels.length },
    { key: "supp_inv",   label: "Supplier Inv.", count: suppInvs.length },
    { key: "system",     label: "System Notes",  count: o.system_notes?.length || 0 },
    { key: "staff",      label: "Staff Notes" },
    { key: "order",      label: "Order Notes" },
    { key: "files",      label: "Files",         count: files.length },
    { key: "related",    label: "Customer 360" },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={close}>
      <div className="w-full max-w-6xl max-h-[92vh] overflow-hidden rounded-lg border border-teal-500/25 bg-[#0F1420] flex flex-col shadow-2xl shadow-black/50" onClick={(e) => e.stopPropagation()} data-testid="order-detail-modal">
        {/* Context bar — only when opened from another record (§2) */}
        {context?.label && (
          <button
            type="button"
            onClick={close}
            className="w-full flex items-center gap-2 px-4 py-2 bg-teal-500/[0.06] border-b border-teal-500/20 text-[11px] font-mono text-teal-200 hover:bg-teal-500/[0.12] transition-colors text-left"
            data-testid="order-modal-context-bar"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
            <span className="text-slate-500">·</span>
            <span className="text-slate-400 normal-case">Opened from</span>
            <span className="text-teal-100">{context.label}</span>
          </button>
        )}
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-white/10">
          <div className="min-w-0">
            <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Order</div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="text-xl font-semibold text-white">#{o.order_number}</h2>
              <span className={`text-[10px] font-mono px-2 py-0.5 rounded border uppercase tracking-widest ${statusTone}`}>{o.status}</span>
              <span className="text-[11px] font-mono text-slate-500">{fmtDate(o.order_date)}</span>
              {o.vip_level && <span className={`text-[9.5px] font-mono px-1.5 py-0.5 rounded uppercase tracking-widest ${TIER_TONE[String(o.vip_level).toUpperCase()] || "text-slate-400 bg-white/5"}`}>{o.vip_level}</span>}
              {o.source && <span className="text-[10px] font-mono text-slate-500">{o.source}{o.sub_source ? ` · ${o.sub_source}` : ""}</span>}
              {o.payment_method && <span className="text-[10px] font-mono text-slate-500">· {o.payment_method}</span>}
            </div>
            <WorkflowChips wf={o.workflow} />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <AITimelineButton orderId={o.id} onOpen={onOpenAI} />
            <SyncButton resource="order" targetId={o.id} variant="subtle" label="Sync order" />
            <button onClick={close} className="p-1.5 rounded hover:bg-white/[0.06] text-slate-400 hover:text-white" data-testid="order-modal-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-0.5 px-2 border-b border-white/10 bg-white/[0.02] overflow-x-auto" data-testid="order-detail-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-[11px] font-mono uppercase tracking-widest whitespace-nowrap border-b-2 transition-colors ${
                tab === t.key
                  ? "border-emerald-400 text-emerald-200 bg-white/[0.03]"
                  : "border-transparent text-slate-400 hover:text-white hover:bg-white/[0.03]"
              }`}
              data-testid={`order-tab-${t.key}`}
            >
              {t.label}
              {t.count != null && <span className="ml-1 text-slate-500">· {t.count}</span>}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 p-4 overflow-y-auto flex-1">
          {/* LEFT — active tab content */}
          <div className="space-y-4 min-w-0" data-testid={`order-tab-body-${tab}`}>
            {tab === "line_items"  && <LineItemsTab items={items} totals={o.money} />}
            {tab === "po_supp"     && <POSupplierTab po={o.po} supp={o.supplier_claim} money={o.money} />}
            {tab === "xero"        && <XeroTab xero={o.xero} />}
            {tab === "confs"       && <GoodsDocsTab docs={confs} kind="confirmation" />}
            {tab === "deliveries"  && <GoodsDocsTab docs={dels} kind="delivery" />}
            {tab === "supp_inv"    && <SupplierInvoicesTab invoices={suppInvs} />}
            {tab === "system"      && <SystemNotesTab logs={o.system_notes} />}
            {tab === "staff"       && <SimpleTextTab text={o.staff_notes}  emptyText="No staff notes recorded." />}
            {tab === "order"       && <SimpleTextTab text={o.order_notes || o.customer_message} emptyText="No order notes." />}
            {tab === "files"       && <FilesTab files={files} />}
            {tab === "related"     && <RelatedTab data={data} />}
          </div>

          {/* RIGHT — always-on summary rail */}
          <div className="space-y-4">
            {o.ai_intel && <OrderIntelPanel intel={o.ai_intel} />}
            {o.is_parked && <ParkedBadgePanel order={o} />}
            <RailSection title="Customer" icon={User}>
              {p ? (
                <>
                  <div className="text-[14px] text-white font-semibold">{p.name}</div>
                  {p.company && <div className="text-[11.5px] text-slate-400 mt-0.5">{p.company}</div>}
                  <div className="text-[11px] font-mono text-slate-500 mt-2 space-y-0.5">
                    {p.email && <div>{p.email}</div>}
                    {p.phone && <div>{p.phone}</div>}
                  </div>
                  <div className="mt-2"><OwnerChip primary={p.account_manager} /></div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                    <button type="button" onClick={() => p.person_id && setC360Id(p.person_id)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-[10.5px] font-mono uppercase tracking-widest hover:bg-emerald-500/20"
                      data-testid="order-modal-open-crm">
                      <ExternalLink className="w-3 h-3" /> Customer 360
                    </button>
                    {c360Id && <Person360Overlay personId={c360Id} onClose={() => setC360Id(null)} />}
                    <SyncButton resource="person" targetId={p.person_id} variant="subtle" label="Sync T1" />
                  </div>
                </>
              ) : (
                <div className="text-[11.5px] text-slate-500 italic">
                  Order has no <code className="text-slate-400">PersonId</code>. Fallback shown from billing address.
                </div>
              )}
            </RailSection>

            <RailSection title="Totals & margin" icon={PoundSterling}>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
                <Row label="Subtotal (ex)"     v={money(o.money?.cust_subtotal_ex)} />
                <Row label="VAT"               v={money(o.money?.cust_total_tax)} />
                <Row label="Carriage (inc)"    v={money(o.money?.cust_carriage_inc)} />
                <Row label="Total (inc)"       v={<span className="text-white font-semibold">{money(o.money?.cust_total_inc)}</span>} />
                {o.money?.discount_amount ? <Row label="Discount" v={money(o.money?.discount_amount)} /> : null}
                {o.money?.refunded ? <Row label="Refunded" v={<span className="text-rose-300">{money(o.money?.refunded)}</span>} /> : null}
                <Row label="Margin"            v={o.money?.margin != null ? <span className="text-emerald-300 font-semibold">{o.money.margin}%</span> : "—"} />
                <Row label="Profit"            v={<span className="text-emerald-300">{money(o.money?.profit)}</span>} />
              </dl>
            </RailSection>

            <RailSection title="Supplier" icon={Truck}>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
                <Row label="Supplier" v={o.po?.supplier_name || "—"} />
                <Row label="Branch"   v={o.po?.supplier_branch || "—"} />
                <Row label="Order no" v={<span className="font-mono text-[11px]">{o.po?.supplier_order_number || "—"}</span>} />
                <Row label="PO total" v={money(o.po?.total_inc)} />
                <Row label="Supp claim" v={money(o.supplier_claim?.total_inc)} />
                <Row label="Diff"     v={<span className={o.po?.supplier_diff ? "text-rose-300" : "text-slate-200"}>{money(o.po?.supplier_diff)}</span>} />
              </dl>
            </RailSection>

            <RailSection title="Key dates" icon={Package}>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
                <Row label="Order"     v={fmtDate(o.dates?.order)} />
                <Row label="Confirmed" v={fmtDate(o.dates?.conf_date)} />
                <Row label="PO"        v={fmtDate(o.dates?.po_date)} />
                <Row label="Shipped"   v={fmtDate(o.dates?.shipped)} />
                <Row label="DN"        v={fmtDate(o.dates?.dn_date)} />
              </dl>
              {Array.isArray(o.dates?.delivery_dates) && o.dates.delivery_dates.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/5">
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Delivery dates</div>
                  <div className="flex flex-wrap gap-1">
                    {o.dates.delivery_dates.map((d, i) => (
                      <span key={i} className="text-[10.5px] font-mono text-sky-300 bg-sky-500/10 border border-sky-500/20 px-1.5 py-0.5 rounded">{fmtDate(d)}</span>
                    ))}
                  </div>
                </div>
              )}
            </RailSection>

            <RailSection title="Addresses" icon={Truck}>
              {o.billing_address && (
                <div className="mb-2">
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Billing</div>
                  <div className="text-[11.5px] text-slate-300 leading-relaxed">
                    {[o.billing_address.company, o.billing_address.street_1, o.billing_address.street_2, o.billing_address.city, o.billing_address.zip, o.billing_address.country].filter(Boolean).join(", ") || "—"}
                  </div>
                </div>
              )}
              {o.shipping_address && (
                <div>
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Shipping</div>
                  <div className="text-[11.5px] text-slate-300 leading-relaxed">
                    {[o.shipping_address.company, o.shipping_address.street_1, o.shipping_address.street_2, o.shipping_address.city, o.shipping_address.zip, o.shipping_address.country].filter(Boolean).join(", ") || "—"}
                  </div>
                </div>
              )}
            </RailSection>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Tabs ────────────────────────────────────────────────────────────────
function LineItemsTab({ items, totals }) {
  return (
    <div className="space-y-3">
      <div className="rounded border border-white/5 overflow-hidden">
        <div className="grid grid-cols-[90px_1fr_52px_84px_84px_90px] gap-2 px-2 py-1.5 border-b border-white/5 text-[10px] font-mono text-slate-500 uppercase tracking-widest bg-white/[0.02]">
          <div>SKU</div><div>Title</div><div className="text-right">Qty</div><div className="text-right">Unit inc</div><div className="text-right">Cost inc</div><div className="text-right">Total inc</div>
        </div>
        {items.length === 0 && <div className="text-center text-slate-500 py-4 text-[12px] italic">No line items on this order.</div>}
        {items.map((li, i) => (
          <div key={i} className="grid grid-cols-[90px_1fr_52px_84px_84px_90px] gap-2 px-2 py-1.5 border-b border-white/[0.03] text-[12px]">
            <div className="text-[10.5px] font-mono text-slate-500 truncate" title={li.sku}>{li.sku || "—"}</div>
            <div className="min-w-0">
              <div className="text-slate-200 truncate" title={li.name}>{li.name || "—"}</div>
              {li.brand && <span className="text-[10px] font-mono text-slate-500">{li.brand}</span>}
              {li.is_refunded && <span className="ml-1 text-[9.5px] font-mono text-rose-300">· refunded</span>}
              {Array.isArray(li.options) && li.options.length > 0 && (
                <div className="mt-0.5 space-y-0.5">
                  {li.options.map((op, j) => (
                    <div key={j} className="text-[10px] text-slate-500 truncate">
                      <span className="text-slate-600">{op.name}:</span> <span className="text-slate-400">{op.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="text-right tabular-nums text-slate-300">{li.quantity ?? "—"}</div>
            <div className="text-right tabular-nums text-slate-400">{money(li.price_inc_tax)}</div>
            <div className="text-right tabular-nums text-slate-400">{money(li.cost_price_inc_tax)}</div>
            <div className="text-right tabular-nums text-slate-100">{money(li.total_inc_tax)}</div>
          </div>
        ))}
      </div>
      {totals && (
        <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 text-[12px] px-2">
          <span className="text-slate-500 font-mono text-[10.5px] uppercase tracking-widest">Subtotal ex <span className="text-slate-200 ml-1 tabular-nums">{money(totals.cust_subtotal_ex)}</span></span>
          <span className="text-slate-500 font-mono text-[10.5px] uppercase tracking-widest">VAT <span className="text-slate-200 ml-1 tabular-nums">{money(totals.cust_total_tax)}</span></span>
          <span className="text-slate-500 font-mono text-[10.5px] uppercase tracking-widest">Total inc <span className="text-white font-semibold ml-1 tabular-nums">{money(totals.cust_total_inc)}</span></span>
        </div>
      )}
    </div>
  );
}

function WorkflowChips({ wf }) {
  if (!wf) return null;
  const chips = [
    { on: wf.po_sent,       label: "PO sent" },
    { on: wf.conf_received, label: "Confirmed" },
    { on: wf.sent_dispatch, label: "Dispatched" },
    { on: wf.sent_invoice,  label: "Invoiced" },
    { on: wf.xero_reconciled, label: "Reconciled" },
    { on: wf.po_split,      label: "Split PO" },
  ];
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid="order-workflow-chips">
      {chips.map((c, i) => (
        <span key={i} className={`text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${
          c.on ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-slate-600 border-white/5 bg-white/[0.02]"
        }`}>
          {c.on ? "✓" : "·"} {c.label}
        </span>
      ))}
      {wf.delivery_note_status && (
        <span className="text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300">
          DN · {wf.delivery_note_status}
        </span>
      )}
    </div>
  );
}


function SimpleTextTab({ text, emptyText }) {
  if (!text) return <div className="text-[12.5px] text-slate-500 italic">{emptyText}</div>;
  // Some Talmia-1 fields (notably OrderNotes) contain HTML markup — sanitise
  // and render as rich when we detect it, plain-text otherwise.
  const looksHtml = typeof text === "string" && /<\/?[a-z][\s\S]*?>/i.test(text);
  if (looksHtml) {
    return (
      <div
        className="rich-content text-[12.5px] text-slate-200 leading-relaxed [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_table]:my-2 [&_td]:px-2 [&_td]:py-1 [&_td]:border-b [&_td]:border-white/5"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(text)) }}
      />
    );
  }
  return <pre className="text-[12.5px] text-slate-200 whitespace-pre-wrap font-sans leading-relaxed">{text}</pre>;
}

function POSupplierTab({ po, supp, money: m }) {
  po = po || {}; supp = supp || {}; m = m || {};
  const rows = [
    { label: "Subtotal (ex VAT)", po: po.subtotal_ex, supp: supp.subtotal_ex },
    { label: "Postage (ex VAT)",  po: po.postage_ex,  supp: supp.postage_ex },
    { label: "VAT",               po: po.total_vat,   supp: supp.total_vat },
    { label: "Total (inc VAT)",   po: po.total_inc,   supp: supp.total_inc, strong: true },
  ];
  return (
    <div className="space-y-4">
      {/* Supplier header */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[12px]">
        <span className="text-slate-500 font-mono text-[10.5px] uppercase tracking-widest">Supplier <span className="text-slate-100 ml-1">{po.supplier_name || "—"}</span></span>
        {po.supplier_branch && <span className="text-slate-500 font-mono text-[10.5px] uppercase tracking-widest">Branch <span className="text-slate-100 ml-1">{po.supplier_branch}</span></span>}
        {po.supplier_order_number && <span className="text-slate-500 font-mono text-[10.5px] uppercase tracking-widest">Order no <span className="text-slate-100 ml-1 font-mono">{po.supplier_order_number}</span></span>}
      </div>

      {/* PO vs Supplier claim comparison */}
      <div className="rounded border border-white/5 overflow-hidden">
        <div className="grid grid-cols-[1fr_110px_110px_110px] gap-2 px-3 py-1.5 border-b border-white/5 text-[10px] font-mono text-slate-500 uppercase tracking-widest bg-white/[0.02]">
          <div></div><div className="text-right">PO (ordered)</div><div className="text-right">Supplier (claim)</div><div className="text-right">Diff</div>
        </div>
        {rows.map((r, i) => {
          const diff = (r.po != null && r.supp != null) ? Math.round((r.supp - r.po) * 100) / 100 : null;
          return (
            <div key={i} className="grid grid-cols-[1fr_110px_110px_110px] gap-2 px-3 py-1.5 border-b border-white/[0.03] text-[12px]">
              <div className={`text-slate-${r.strong ? "200 font-semibold" : "400"}`}>{r.label}</div>
              <div className={`text-right tabular-nums ${r.strong ? "text-white font-semibold" : "text-slate-300"}`}>{money(r.po)}</div>
              <div className={`text-right tabular-nums ${r.strong ? "text-white font-semibold" : "text-slate-300"}`}>{money(r.supp)}</div>
              <div className={`text-right tabular-nums ${diff ? "text-rose-300" : "text-slate-500"}`}>{diff != null ? money(diff) : "—"}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Margin" value={m.margin != null ? `${m.margin}%` : "—"} tone="text-emerald-300" />
        <StatCard label="Profit" value={money(m.profit)} tone="text-emerald-300" />
        <StatCard label="PO/Supp diff" value={money(po.supplier_diff)} tone={po.supplier_diff ? "text-rose-300" : ""} />
        <StatCard label="Diff OK?" value={po.diff_ok === true ? "Yes" : po.diff_ok === false ? "No" : "—"} tone={po.diff_ok === false ? "text-rose-300" : "text-slate-300"} />
      </div>

      {(supp.total_ex_with_rebate != null || supp.total_ex_with_rebate_discount != null) && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11.5px] px-1">
          {supp.total_ex_with_rebate != null && <span className="text-slate-500 font-mono text-[10.5px] uppercase tracking-widest">Supp total ex (w/ rebate) <span className="text-slate-200 ml-1 tabular-nums">{money(supp.total_ex_with_rebate)}</span></span>}
          {supp.total_ex_with_rebate_discount != null && <span className="text-slate-500 font-mono text-[10.5px] uppercase tracking-widest">w/ rebate+disc <span className="text-slate-200 ml-1 tabular-nums">{money(supp.total_ex_with_rebate_discount)}</span></span>}
        </div>
      )}

      {po.analysis && (
        <div>
          <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">PO / Supplier analysis</div>
          {typeof po.analysis === "string" && /<\/?[a-z][\s\S]*?>/i.test(po.analysis) ? (
            <div
              className="rich-content text-[12.5px] text-slate-200 leading-relaxed [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_table]:my-2 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:text-slate-400 [&_th]:font-mono [&_th]:text-[10px] [&_th]:uppercase [&_th]:tracking-widest [&_td]:px-2 [&_td]:py-1 [&_td]:border-b [&_td]:border-white/5 [&_table]:border-collapse [&_table]:w-full [&_b]:text-slate-100"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(po.analysis)) }}
            />
          ) : (
            <pre className="text-[12.5px] text-slate-300 whitespace-pre-wrap font-sans">
              {typeof po.analysis === "string" ? po.analysis : JSON.stringify(po.analysis, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function GoodsDocsTab({ docs, kind }) {
  if (!Array.isArray(docs) || docs.length === 0) {
    return <div className="text-[12.5px] text-slate-500 italic">No {kind === "delivery" ? "delivery notes" : "order confirmations"} on this order.</div>;
  }
  return (
    <div className="space-y-3" data-testid={`goods-docs-${kind}`}>
      {docs.map((d, i) => (
        <div key={i} className="rounded border border-white/5 bg-white/[0.02]">
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-white/5">
            <div className="flex items-center gap-2 min-w-0">
              <BadgeCheck className="w-3.5 h-3.5 text-teal-300 shrink-0" />
              <span className="text-[12.5px] text-slate-100 font-mono truncate">{d.delivery_note_no || d.order_number || "—"}</span>
              {d.status && <span className="text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300">{d.status}</span>}
            </div>
            <div className="flex items-center gap-3 text-[10.5px] font-mono text-slate-500">
              {d.branch && <span>{d.branch}</span>}
              {d.due_date && <span>Due {fmtDate(d.due_date)}</span>}
              {d.dn_date && <span>DN {fmtDate(d.dn_date)}</span>}
            </div>
          </div>
          <div className="px-3 py-1.5 text-[10.5px] font-mono text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5">
            {d.your_ref && <span>Ref: <span className="text-slate-300">{d.your_ref}</span></span>}
            {d.delivery_name && <span>To: <span className="text-slate-300">{d.delivery_name}</span></span>}
            {d.delivery_address && <span className="text-slate-400">{d.delivery_address}</span>}
          </div>
          {Array.isArray(d.items) && d.items.length > 0 && (
            <div className="px-3 pb-2">
              {d.items.map((it, j) => (
                <div key={j} className="grid grid-cols-[1fr_52px_90px_90px] gap-2 py-1 border-b border-white/[0.03] text-[11.5px]">
                  <div className="min-w-0"><span className="text-slate-500 font-mono text-[10px]">{it.product_code}</span> <span className="text-slate-300 truncate">{it.description}</span></div>
                  <div className="text-right tabular-nums text-slate-400">{it.quantity}</div>
                  <div className="text-right tabular-nums text-slate-400">{money(it.price)}</div>
                  <div className="text-right tabular-nums text-slate-200">{money(it.value)}</div>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-x-5 gap-y-0.5 px-3 pb-2 text-[11px]">
            <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest">Goods <span className="text-slate-300 ml-1 tabular-nums">{money(d.total_goods)}</span></span>
            {d.carriage ? <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest">Carriage <span className="text-slate-300 ml-1 tabular-nums">{money(d.carriage)}</span></span> : null}
            <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest">VAT <span className="text-slate-300 ml-1 tabular-nums">{money(d.vat)}</span></span>
            <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest">Total <span className="text-white font-semibold ml-1 tabular-nums">{money(d.total_amount)}</span></span>
          </div>
        </div>
      ))}
    </div>
  );
}

function SupplierInvoicesTab({ invoices }) {
  if (!Array.isArray(invoices) || invoices.length === 0) {
    return <div className="text-[12.5px] text-slate-500 italic">No supplier invoices on this order.</div>;
  }
  return (
    <div className="rounded border border-white/5 overflow-hidden" data-testid="supplier-invoices">
      <div className="grid grid-cols-[1fr_110px_90px_80px_90px] gap-2 px-3 py-1.5 border-b border-white/5 text-[10px] font-mono text-slate-500 uppercase tracking-widest bg-white/[0.02]">
        <div>Delivery note</div><div>Invoice date</div><div className="text-right">Goods</div><div className="text-right">VAT</div><div className="text-right">Total</div>
      </div>
      {invoices.map((inv, i) => (
        <div key={i} className="grid grid-cols-[1fr_110px_90px_80px_90px] gap-2 px-3 py-1.5 border-b border-white/[0.03] text-[12px]">
          <div className="text-slate-200 font-mono truncate">{inv.delivery_note_no || "—"}{inv.your_ref && <span className="text-slate-500 ml-1">· {inv.your_ref}</span>}</div>
          <div className="text-slate-400 font-mono text-[11px]">{fmtDate(inv.invoice_date)}</div>
          <div className="text-right tabular-nums text-slate-300">{money(inv.total_goods)}</div>
          <div className="text-right tabular-nums text-slate-400">{money(inv.vat)}</div>
          <div className="text-right tabular-nums text-white font-semibold">{money(inv.total_amount)}</div>
        </div>
      ))}
    </div>
  );
}

function FilesTab({ files }) {
  if (!Array.isArray(files) || files.length === 0) {
    return <div className="text-center text-slate-500 py-8 text-[12.5px] italic">No files attached to this order.</div>;
  }
  return (
    <ul className="space-y-1.5 text-[12.5px]" data-testid="order-files">
      {files.map((f, i) => (
        <li key={i} className="flex items-center gap-2 px-2 py-1.5 rounded border border-white/5 bg-white/[0.02]">
          <FileText className="w-3.5 h-3.5 text-teal-300 shrink-0" />
          <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest w-44 shrink-0">{f.label}</span>
          <span className="text-slate-200 truncate" title={f.path}>{f.filename}</span>
        </li>
      ))}
    </ul>
  );
}


function SystemNotesTab({ logs }) {
  const [expanded, setExpanded] = useState(false);

  // ── Data-quality fix ─────────────────────────────────────────────────
  // Real Talmia-1 orders arrived with SystemNotes stored EITHER as:
  //   • a plain string blob (single field mirror)
  //   • a list of 1-char strings (Python iterated over a string during mirror)
  //   • a proper list of {ts, actor, msg} events (our seed + future writes)
  // Normalise all three into a common shape.
  const normalised = useMemo(() => {
    if (typeof logs === "string" && logs.length > 0) {
      return [{ ts: null, actor: "Talmia-1 (import)", msg: logs, _reconstructed: true }];
    }
    if (!Array.isArray(logs) || logs.length === 0) return [];
    const allSingleChars = logs.every((l) => typeof l === "string" && l.length <= 2);
    if (allSingleChars) {
      return [{ ts: null, actor: "Talmia-1 (import)", msg: logs.join(""), _reconstructed: true }];
    }
    return logs.map((l) => (typeof l === "string" ? { ts: null, msg: l } : l));
  }, [logs]);

  if (normalised.length === 0) {
    return <div className="text-[12.5px] text-slate-500 italic">No system notes recorded.</div>;
  }

  const CAP = 100;
  const visible = expanded ? normalised : normalised.slice(-CAP);
  const clipped = !expanded && normalised.length > CAP;

  return (
    <div className="space-y-2">
      {normalised[0]?._reconstructed && (
        <div className="text-[10.5px] font-mono text-amber-300/90 bg-amber-500/[0.06] border border-amber-500/20 px-2 py-1 rounded">
          Data-quality fix: SystemNotes were imported as a character array. Displayed as reconstructed text below.
        </div>
      )}
      {clipped && (
        <div className="flex items-center justify-between text-[10.5px] font-mono text-slate-400 bg-white/[0.02] border border-white/5 px-2 py-1 rounded">
          <span>Showing last {CAP} of {normalised.length.toLocaleString()} events</span>
          <button onClick={() => setExpanded(true)}
            className="text-emerald-300 hover:text-emerald-200 uppercase tracking-widest"
            data-testid="system-notes-show-all">
            Show all
          </button>
        </div>
      )}
      <ol className="space-y-1 text-[12px]">
        {visible.map((l, i) => (
          <li key={i} className="border-l-2 border-white/5 pl-3">
            {(l.at || l.ts) && <span className="text-[10px] font-mono text-slate-500">{fmtDate(l.at || l.ts)}</span>}
            {(l.user || l.actor) && <span className="text-[10px] font-mono text-slate-500 ml-2">· {l.user || l.actor}</span>}
            <div className="text-slate-300 whitespace-pre-wrap break-words">{l.event || l.msg || l.message || ""}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function XeroTab({ xero }) {
  if (!xero) return <div className="text-[12.5px] text-slate-500 italic">No Xero invoice linked to this order.</div>;
  const rows = [
    { label: "Subtotal (ex VAT)", comm: xero.comm?.subtotal_ex, disc: xero.disc?.subtotal_ex },
    { label: "Postage (ex VAT)",  comm: xero.comm?.postage_ex,  disc: xero.disc?.postage_ex },
    { label: "VAT",               comm: xero.comm?.total_vat,   disc: xero.disc?.total_vat },
    { label: "Total (inc VAT)",   comm: xero.comm?.total_inc,   disc: xero.disc?.total_inc, strong: true },
  ];
  return (
    <div className="space-y-4" data-testid="xero-tab">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[12px]">
        <span className="text-slate-500 font-mono text-[10.5px] uppercase tracking-widest">Invoice <span className="text-slate-100 ml-1 font-mono">{xero.number || "—"}</span></span>
        {xero.status && <span className={`text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${
          xero.status === "AUTHORISED" || xero.status === "PAID" ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-amber-300 border-amber-500/40 bg-amber-500/10"
        }`}>{xero.status}</span>}
        {xero.created && <span className="text-[10.5px] font-mono text-slate-500">Created in Xero</span>}
      </div>
      <div className="rounded border border-white/5 overflow-hidden">
        <div className="grid grid-cols-[1fr_120px_120px] gap-2 px-3 py-1.5 border-b border-white/5 text-[10px] font-mono text-slate-500 uppercase tracking-widest bg-white/[0.02]">
          <div></div><div className="text-right">Commercial</div><div className="text-right">Discounted</div>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_120px_120px] gap-2 px-3 py-1.5 border-b border-white/[0.03] text-[12px]">
            <div className={r.strong ? "text-slate-200 font-semibold" : "text-slate-400"}>{r.label}</div>
            <div className={`text-right tabular-nums ${r.strong ? "text-white font-semibold" : "text-slate-300"}`}>{money(r.comm)}</div>
            <div className={`text-right tabular-nums ${r.strong ? "text-white font-semibold" : "text-slate-300"}`}>{money(r.disc)}</div>
          </div>
        ))}
      </div>
      {xero.note && <div className="text-[11.5px] text-slate-400 italic">{xero.note}</div>}
      <div className="text-[10.5px] font-mono text-slate-600">Raw Xero labels preserved (Commercial = list price, Discounted = net). Figures are shown as-is — never recomputed.</div>
    </div>
  );
}

function RelatedTab({ data }) {
  const calls = data.related_calls || [];
  const quotes = data.related_quotes || [];
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
          <PhoneCall className="w-3 h-3" /> Related calls ({calls.length})
        </div>
        {calls.length === 0 && <div className="text-[12px] text-slate-500 italic">No related calls in last 30 days.</div>}
        {calls.slice(0, 10).map((c) => (
          <a key={c.id} href={`/calls?id=${c.id}`}
            className="block px-2 py-1.5 rounded hover:bg-white/[0.03] border-b border-white/[0.03]"
          >
            <div className="flex items-center gap-2 text-[12px]">
              <span className="text-[9.5px] font-mono text-slate-500 uppercase tracking-widest">{c.direction || "—"}</span>
              <span className="text-slate-200 truncate flex-1">{c.summary || "Call recording"}</span>
              <span className="text-[10px] font-mono text-slate-500">{fmtDate(c.created_at)}</span>
            </div>
          </a>
        ))}
      </div>
      <div>
        <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
          <FileText className="w-3 h-3" /> Related quotes ({quotes.length})
        </div>
        {quotes.length === 0 && <div className="text-[12px] text-slate-500 italic">No prior quotes for this customer.</div>}
        {quotes.slice(0, 10).map((qd) => (
          <a key={qd.id} href={`/quotes/${qd.id}`}
            className="block px-2 py-1.5 rounded hover:bg-white/[0.03] border-b border-white/[0.03]"
          >
            <div className="flex items-center gap-2 text-[12px]">
              <span className="text-slate-200 truncate flex-1">Q{qd.quote_id || qd.id.slice(0, 6)}</span>
              <span className="text-[10px] font-mono text-slate-400 tabular-nums">{money(qd.total_inc)}</span>
              <span className="text-[10px] font-mono text-slate-500">{fmtDate(qd.created_at)}</span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  return (
    <div className="rounded border border-white/5 bg-white/[0.02] p-2">
      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">{label}</div>
      <div className={`text-[13px] font-semibold tabular-nums ${tone || "text-slate-100"}`}>{value}</div>
    </div>
  );
}

function RailSection({ title, icon: Icon, children }) {
  return (
    <section>
      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {title}
      </div>
      {children}
    </section>
  );
}

function Row({ label, v }) {
  return (
    <>
      <dt className="text-slate-500 text-[10.5px] font-mono uppercase tracking-widest">{label}</dt>
      <dd className="text-slate-200 text-right tabular-nums font-mono">{v}</dd>
    </>
  );
}


// ─── AI Intel Panel — persistent Gemini-derived Order 360 signals ──────
// Renders in the Order Detail modal rail so staff (and future AI agents)
// see risk / margin / next-best-action at a glance without opening the
// full AI Timeline modal.
const RISK_TONE = {
  low:      "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  medium:   "text-amber-300  border-amber-500/40  bg-amber-500/10",
  high:     "text-orange-300 border-orange-500/40 bg-orange-500/10",
  critical: "text-rose-300   border-rose-500/40   bg-rose-500/10",
};
const HEALTH_TONE = {
  healthy: "text-emerald-300",
  watch:   "text-amber-300",
  thin:    "text-orange-300",
  loss:    "text-rose-300",
};

function OrderIntelPanel({ intel }) {
  if (!intel) return null;
  const risk = (intel.risk_level || "unknown").toLowerCase();
  const health = (intel.margin_health || "").toLowerCase();
  const tags = Array.isArray(intel.tags) ? intel.tags : [];
  return (
    <section className="rounded-lg border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.06] to-transparent p-3" data-testid="order-intel-panel">
      <div className="text-[10px] font-mono text-emerald-300 uppercase tracking-widest mb-2 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> AI Order Intelligence
        {intel.generated_at && <span className="text-slate-500 normal-case tracking-normal ml-auto text-[9.5px]">
          {fmtDate(intel.generated_at)}
        </span>}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        <span className={`text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${RISK_TONE[risk] || "text-slate-400 border-white/10 bg-white/[0.02]"}`}>
          Risk · {intel.risk_level || "—"} {intel.risk_score != null && `(${intel.risk_score})`}
        </span>
        {intel.margin_health && (
          <span className={`text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.02] ${HEALTH_TONE[health] || "text-slate-400"}`}>
            Margin · {intel.margin_health}
          </span>
        )}
        {intel.delivery_risk && (
          <span className="text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.02] text-sky-300">
            Delivery · {intel.delivery_risk}
          </span>
        )}
        {intel.value_band && (
          <span className="text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.02] text-fuchsia-300">
            {intel.value_band}
          </span>
        )}
      </div>
      {intel.next_best_action && (
        <div className="text-[12px] text-slate-100 leading-snug mb-2">
          <span className="text-[9.5px] font-mono text-slate-500 uppercase tracking-widest block mb-0.5">Next best action</span>
          {intel.next_best_action}
        </div>
      )}
      {intel.customer_context && (
        <div className="text-[11.5px] text-slate-300 italic leading-snug mb-2">
          {intel.customer_context}
        </div>
      )}
      {tags.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          {tags.map((t, i) => (
            <span key={i} className="text-[9.5px] font-mono text-slate-400 px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/5">
              #{t}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

function ParkedBadgePanel({ order }) {
  return (
    <section className="rounded-lg border border-amber-500/40 bg-amber-500/[0.06] p-3" data-testid="parked-badge-panel">
      <div className="text-[10px] font-mono text-amber-300 uppercase tracking-widest mb-2 flex items-center gap-1.5">
        <AlertTriangle className="w-3 h-3" /> Parked
        {order.escalated && <span className="ml-auto text-rose-300 flex items-center gap-1"><Flag className="w-3 h-3" /> Escalated</span>}
      </div>
      {order.parked_reason && (
        <div className="text-[12px] text-slate-100 mb-1.5">{order.parked_reason}</div>
      )}
      <div className="text-[10.5px] font-mono text-slate-500 space-y-0.5">
        {order.parked_at && <div>Since: <span className="text-slate-300">{fmtDate(order.parked_at)}</span></div>}
        {order.parked_by && <div>By: <span className="text-slate-300">{order.parked_by}</span></div>}
        {order.last_chased_at && <div>Last chased: <span className="text-sky-300">{fmtDate(order.last_chased_at)}</span></div>}
      </div>
    </section>
  );
}
